import { useTableActionContext } from '@aibees/elephant/context';
import { useActionButtonContext } from '@aibees/elephant/context/action-button-context';
import { SearchModality } from '@aibees/elephant/feature/search-table-company/hooks/types';
import {
  getEmailsCleared,
  howManyEmailsFound,
  isAnyEmailNeedsReverification,
} from '@aibees/elephant/feature/search-table-people/components/people-email-popover/utils';
import {
  SaveRecordAction,
  useSearchModality,
  useSearchRouteInfo,
} from '@aibees/elephant/hooks';
import { useGetCreditsAmountTypeOnActionPopup } from '@aibees/elephant/hooks/use-get-credits-amount-type-on-action-popup';
import {
  ENRICHMENT_STATE,
  FeatureFlags,
  SearchTable,
  SearchType,
} from '@aibees/elephant/types';
import { CreditUsageType } from '@aibees/shared/definition';
import { useFeatureFlagsContext } from '@aibees/shared/feature-flags';
import { Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { NotificationPopupNoteCost } from '../../notification-popup-note-cost';
import { BasicCompanyStorageInfo } from '../components/basic-company-storage-info/basic-company-storage-info';
import { ContactOption } from '../types';

export function useGetEnrichmentItems() {
  const { item: selectedItem } = useActionButtonContext();
  const { table } = useSearchRouteInfo();

  // Use the flow's effective search type (folds in the ActionButton override) so
  // the title/items follow the record, not just the page route — e.g. a person
  // opened from the employees sidebar on a company search page.
  const { integrationConfig, effectiveSearchType } = useTableActionContext();
  const { modality } = useSearchModality();
  const isPeople = effectiveSearchType === SearchType.People;

  const isCompanyEmployees = table === SearchTable.Employees;

  const isPhoneNumberEnabled = useFeatureFlagsContext().isFeatureEnabled(
    FeatureFlags.PEOPLE_PHONE_NUMBER
  );

  const title = isPeople
    ? 'Contact Data Enrichment:'
    : 'Account Data Enrichment:';
  const {
    aiCompany,
    aiPeople,
    aiUnsavedPeople,
    basicPeople,
    basicCompany,
    basicUnsavedPeople,
  } = useGetCreditsAmountTypeOnActionPopup();
  const isAnyEmailFound = !!howManyEmailsFound(
    getEmailsCleared(selectedItem?.revelation?.email)
  );

  const items: ContactOption[] = useMemo(
    () => [
      {
        title: 'Email Enrichment',
        value: SaveRecordAction.CONTACT_ENRICH_EMAIL,
        description: '',
        disabled: !!integrationConfig,
        icon: 'email',
        active:
          basicPeople ||
          aiPeople ||
          aiUnsavedPeople ||
          basicUnsavedPeople ||
          isPeople,
        done: !!selectedItem && isAnyEmailFound,
        notificationTitle: 'Email Enrichment',
        notificationDescription: (
          <Typography variant="body4">
            Finds and verifies a business email in real-time via BounceBan.
            <NotificationPopupNoteCost
              type={CreditUsageType.CONTACT_EMAIL_FINDING}
            />{' '}
            credits per valid email found — we never deliver invalid or risky,
            only verified ones (including catch-all valids). Charged once per
            contact on first acquisition (saving to a list, saving as a record,
            or exporting) — never re-charged afterward.
          </Typography>
        ),
      },
      {
        title: 'Email Re-Verify',
        value: SaveRecordAction.CONTACT_REVERIFY_EMAIL,
        description: '',
        disabled:
          modality === SearchModality.UNSAVED ||
          (!!selectedItem && !selectedItem?.revelation?.email),
        icon: 'published-with-changes',
        done:
          !!selectedItem &&
          !!selectedItem?.revelation?.email &&
          !isAnyEmailNeedsReverification(selectedItem.revelation?.email),
        active: basicPeople || aiPeople || isPeople,
        notificationTitle: 'Email Re-Reverify',
        notificationDescription: (
          <Typography variant="body4">
            Only relevant for Saved Records — safe to always keep on. Our system
            automatically re-checks emails on contacts you already own (Saved
            Records) to confirm they're still valid today. Net New records are
            being automatically freshly verified through "Email Enrichment".{' '}
            <NotificationPopupNoteCost
              type={CreditUsageType.CONTACT_EMAIL_REVERIFICATION}
            />{' '}
            credits per Saved Record check — ideal before launching a campaign
            on older data.
          </Typography>
        ),
      },
      {
        title: 'Profile Insight',
        value: SaveRecordAction.CONTACT_REVEAL_PROFILE,
        description: '',
        disabled: false,
        icon: 'badge-outline',
        active:
          basicPeople ||
          aiPeople ||
          aiUnsavedPeople ||
          basicUnsavedPeople ||
          isPeople,
        done:
          !!selectedItem &&
          selectedItem.revelation?.contact?.state === ENRICHMENT_STATE.SUCCESS,
        notificationTitle: 'Profile insight',
        notificationDescription: (
          <Typography variant="body4">
            Unlocks 250+ advanced data points beyond the defaults — languages
            spoken, education, skills, certifications, work history details,
            volunteer experience, and more. Choose the exact fields you want in
            your Export Template under "Create Template."
          </Typography>
        ),
      },
      {
        title: 'Mobile Phone Number',
        icon: 'phone',
        disabled: !isPhoneNumberEnabled,
        value: SaveRecordAction.CONTACT_REVEAL_PHONE_CACHED,
        active:
          isPhoneNumberEnabled &&
          (basicPeople ||
            aiPeople ||
            aiUnsavedPeople ||
            basicUnsavedPeople ||
            isPeople),
        notificationTitle: 'Mobile Phone Number',
        notificationDescription: (
          <Typography variant="body4">
            Returns a verified mobile number when available.{' '}
            <NotificationPopupNoteCost
              type={CreditUsageType.CONTACT_PHONE_FINDING}
            />{' '}
            credits per number found. Coverage spans 100M+ profiles and grows
            monthly — not every contact has one on file.
          </Typography>
        ),
      },
      {
        title: 'AI Personality Enrichment',
        value: SaveRecordAction.CONTACT_REVEAL_ANALYSIS,
        description: '',
        disabled: false,
        icon: 'person-search-outline',
        done:
          !!selectedItem &&
          selectedItem.revelation?.analysis?.state === ENRICHMENT_STATE.SUCCESS,
        active: !!selectedItem && (isPeople || basicPeople),
      },
      {
        title: 'Basic Contact Storage',
        value: SaveRecordAction.BASIC_SAVE,
        description: '',
        disabled: true,
        icon: 'contactless',
        mandatory: true,
        active: basicPeople || basicUnsavedPeople || isPeople,
        done:
          modality === SearchModality.SAVED ||
          (!!selectedItem && selectedItem.saved),
        notificationTitle: 'Basic Contact Storage',
        notificationDescription: (
          <Typography variant="body4">
            The foundation of every contact: name, job title, company name,
            company domain, LinkedIn URL, and other core profile data.{' '}
            <NotificationPopupNoteCost
              type={CreditUsageType.CONTACT_STORAGE_BASIC}
            />{' '}
            credits per contact, charged once per contact on first acquisition
            (saving to a list, saving as a record, or exporting) — never
            recharged afterward.{' '}
          </Typography>
        ),
      },
      // Basic Account Storage - for basic company searches
      {
        title: 'Basic Account Storage',
        value: SaveRecordAction.AI_POWERED_ACCOUNT,
        description: '',
        disabled: true,
        icon: 'contactless',
        mandatory: true,
        active: basicCompany && !isCompanyEmployees,
        done:
          modality === SearchModality.SAVED ||
          (!!selectedItem && selectedItem.saved),
        notificationTitle: 'Basic Account Storage',
        notificationDescription: <BasicCompanyStorageInfo />,
      },
      // AI-Powered Account Storage - for AI company searches
      {
        title: 'AI-Powered Account Storage',
        value: SaveRecordAction.AI_POWERED_ACCOUNT,
        description: '',
        disabled: true,
        icon: 'contactless',
        mandatory: true,
        active: aiCompany && !isCompanyEmployees,
        done:
          modality === SearchModality.SAVED ||
          (!!selectedItem && selectedItem.saved),
        notificationTitle: 'AI-Powered Account Storage',
        notificationDescription: (
          <Stack>
            <Typography variant="body4">
              This applies to saving, exporting, adding to lists, labeling, or
              assigning owners to company records.
            </Typography>
            <Typography variant="body4">
              These records are sourced from AI-driven similarity (lookalike) or
              semantic (text) searches.
            </Typography>
            <Typography variant="body4">
              Credit cost are{' '}
              <NotificationPopupNoteCost
                type={CreditUsageType.ACCOUNT_STORAGE_AI}
              />{' '}
              per record, regardless of whether the record was previously saved
            </Typography>
          </Stack>
        ),
      },
      // AI-Powered Contact Storage - for AI people searches
      {
        title: 'AI-Powered Contact Storage',
        value: SaveRecordAction.AI_POWERED_CONTACT,
        description: '',
        disabled: true,
        icon: 'contactless',
        mandatory: true,
        active: (aiPeople || aiUnsavedPeople) && !isCompanyEmployees,
        done:
          modality === SearchModality.SAVED ||
          (!!selectedItem && selectedItem.saved),
        notificationTitle: 'AI-Powered Contact Storage',
        notificationDescription: (
          <Stack>
            <Typography variant="body4">
              AI-Powered Contact Storage allows you to save, export, label, add
              to lists, or assign owners to contact records sourced from
              AI-driven lookalike or semantic searches.
            </Typography>
            <Typography variant="body4">
              Each action{' '}
              <NotificationPopupNoteCost
                type={CreditUsageType.ACCOUNT_STORAGE_AI}
              />{' '}
              credits per record regardless of whether the contact has been
              previously saved or exported.
            </Typography>
            <Typography variant="body4">
              This feature provides enriched and precise contact insights by
              leveraging advanced AI models to identify and recommend similar or
              related profiles tailored to your search criteria
            </Typography>
          </Stack>
        ),
      },
    ],
    [
      basicPeople,
      aiPeople,
      aiUnsavedPeople,
      basicUnsavedPeople,
      basicCompany,
      aiCompany,
      selectedItem,
      isAnyEmailFound,
      modality,
      isPhoneNumberEnabled,
      isPeople,
      isCompanyEmployees,
      integrationConfig,
    ]
  );

  return { items: items.filter((i) => i.active), title };
}
